/**
 * DelegatePickerModal — the owner's delegate picker (wireframe: the "+" on the "Delegated to" row; Ownership design,
 * parts 2 and 8). Lists only the people the server says may be the delegate (an active user whose role can edit
 * JDs, with this JD in their view scope, never the owner nor the approver of an open review), searchable by name
 * or role. Delegate stays disabled until someone is picked; with nobody available it says so. Laid out by
 * PersonPickerModal, as the share and approver pickers.
 *   open       — shown or not
 *   jobId      — the JD (its job_id)
 *   delegating — the delegation is in flight
 *   onCancel()
 *   onDelegate(email, name)
 *   onRefused({ message }) — the list was refused because control changed meanwhile (e.g. ALREADY_DELEGATED,
 *                            NOT_OWNER): the page says why and reloads, as for every other ownership refusal
 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import PersonPickerModal from './PersonPickerModal';
import { getDelegateCandidates } from 'Api/apiService';
import { DELEGATE_PICKER, TOOLTIP_DELEGATE_ADD, getDelegationError } from 'Utils/jdOwnership';
import { isOutOfScopeError } from 'Utils/sendAndPublish';
import { OUT_OF_SCOPE_MESSAGE } from 'Utils/jobDetailsPageState';

const DelegatePickerModal = ({
  open, jobId = undefined, delegating = false, onCancel, onDelegate, onRefused = undefined,
}) => {
  const [people, setPeople] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failure, setFailure] = useState(null);

  useEffect(() => {
    if (!open || !jobId) return undefined;
    let cancelled = false;
    setSelected(null);
    setPeople([]);
    setFailure(null);
    setLoading(true);
    getDelegateCandidates(jobId)
      .then((data) => { if (!cancelled) setPeople(Array.isArray(data) ? data : []); })
      .catch((err) => {
        if (cancelled) return;
        const refusal = getDelegationError(err);
        if (refusal.reload && onRefused) {
          setFailure(refusal.message);   // until the page closes the picker and reloads
          onRefused(refusal);
          return;
        }
        setFailure(isOutOfScopeError(err) ? OUT_OF_SCOPE_MESSAGE
          : err?.response?.data?.message || DELEGATE_PICKER.loadFailedMessage);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, jobId]); // eslint-disable-line react-hooks/exhaustive-deps -- onRefused: the parent's latest is enough

  const options = people.map((p) => ({ value: p.value, label: p.role ? `${p.label} (${p.role})` : p.label }));
  const message = failure || (!loading && !people.length ? DELEGATE_PICKER.noOneMessage : null);
  const pickedName = people.find((p) => p.value === selected)?.label;

  return (
    <PersonPickerModal
      open={open}
      title={DELEGATE_PICKER.title}
      description={`${TOOLTIP_DELEGATE_ADD}.`}
      label={DELEGATE_PICKER.label}
      placeholder={DELEGATE_PICKER.placeholder}
      options={options}
      selected={selected}
      onSelect={setSelected}
      loading={loading}
      message={message}
      messageTestId="delegate-picker-message"
      busy={delegating}
      cancelLabel={DELEGATE_PICKER.cancelLabel}
      confirmLabel={DELEGATE_PICKER.delegateLabel}
      onCancel={onCancel}
      onConfirm={() => onDelegate(selected, pickedName)}
    />
  );
};

DelegatePickerModal.propTypes = {
  open: PropTypes.bool.isRequired,
  jobId: PropTypes.string,
  delegating: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onDelegate: PropTypes.func.isRequired,
  onRefused: PropTypes.func,
};

export default DelegatePickerModal;
